import {Alert, Button, IconButton, Tooltip} from 'platform/components';
import {
  Box,
  getSize,
  HStack,
  Scroll,
  Show,
  Space,
  Spinner,
  Text,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment, KeyboardEventHandler} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AiComposerRefinement} from './AiComposerDraft';
import {AiComposerBusyRing} from './components/AiComposerBusyRing';
import {AiComposerFocusRegion} from './components/AiComposerFocusRegion';
import {AiComposerMark} from './components/AiComposerMark';
import {AiComposerPill} from './components/AiComposerPill';
import {AiComposerPopover} from './components/AiComposerPopover';
import {AiComposerPromptInput} from './components/AiComposerPromptInput';
import {AiComposerProposalBlock} from './components/AiComposerProposalBlock';
import {AiComposerSuggestionButton} from './components/AiComposerSuggestionButton';
import {AiComposerSurface} from './components/AiComposerSurface';
import {AiMessageComposerController} from './useAiMessageComposer';

const POPOVER_WIDTH = 96;
// The popover never grows past the viewport on a phone; the gutter is two steps of the scale.
const POPOVER_MAX_WIDTH = `calc(100vw - ${getSize(8)})` as const;
const ESCAPE_KEY = 'Escape';

const REFINEMENTS: {refinement: AiComposerRefinement; titleKey: string}[] = [
  {refinement: 'improve', titleKey: 'general.aiPrettify.action'},
  {refinement: 'shorter', titleKey: 'general.aiMessageComposer.actions.shorter'},
  {refinement: 'formal', titleKey: 'general.aiMessageComposer.actions.formal'},
  {refinement: 'detail', titleKey: 'general.aiMessageComposer.actions.detail'},
];

interface AiMessageComposerPanelProps extends RequiredTestIdProps {
  controller: AiMessageComposerController;
}

const getErrorMessageKey = (errorKind: AiMessageComposerController['errorKind']) =>
  match(errorKind)
    .with('placeholderMismatch', () => 'general.aiMessageComposer.labels.placeholderMismatch')
    .with('forbidden', () => 'general.notifications.noPermission')
    .with('recordUnavailable', () => 'general.aiMessageComposer.labels.recordUnavailable')
    .with('network', () => 'general.notifications.noInternet')
    .with('timeout', () => 'general.aiMessageComposer.labels.timeout')
    .otherwise(() => 'aiAssistant.guardrails.unavailable');

export function AiMessageComposerPanel(props: AiMessageComposerPanelProps) {
  const controller = props.controller;
  const proposal = controller.proposal;

  if (!controller.isOpen) {
    return null;
  }

  const isBusy = controller.isPending;
  // A record surface writes a whole message until one is accepted; from then on, it refines.
  const isGeneratePrompt =
    controller.canGenerate && (isNil(controller.undoStep) || !controller.hasExistingBody);
  const generatePromptLabelKey = match(controller.channel)
    .with('sms', () => 'general.aiMessageComposer.labels.promptPlaceholderGenerateSms' as const)
    .with('email', () => 'general.aiMessageComposer.labels.promptPlaceholderGenerate' as const)
    .with('chat', () => 'aiAssistant.composer.placeholder' as const)
    .exhaustive();
  const promptLabel = isGeneratePrompt
    ? i18n.t(generatePromptLabelKey)
    : i18n.t('aiAssistant.composer.templateEditRefinementPlaceholder');
  const promptHelp = isGeneratePrompt
    ? i18n.t('general.aiMessageComposer.labels.promptHelpGenerate')
    : i18n.t('general.aiMessageComposer.labels.promptHelpRefine');

  const handleSubmitInstruction = () => {
    if (isGeneratePrompt) {
      if (controller.canRunGenerate) {
        controller.onGenerate();
      }
      return;
    }

    controller.onRefineWithInstruction('improve');
  };

  // Escape belongs to the composer while it is open, from any control inside it. Catching it in
  // the capture phase stops it reaching the dialog that holds the whole e-mail form.
  const handleKeyDownCapture: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key !== ESCAPE_KEY) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    controller.onEscape();
  };

  const refinementPills = REFINEMENTS.filter(
    (action) => action.refinement !== 'detail' || controller.canRefineDetail
  ).map((action) => (
    <AiComposerPill
      key={action.refinement}
      title={i18n.t(action.titleKey)}
      isDisabled={isBusy}
      onClick={() => controller.onRefine(action.refinement)}
      data-testid={suffixTestId(`refine-${action.refinement}`, props)}
    />
  ));

  const popover = (
    <Box width={POPOVER_WIDTH} maxWidth={POPOVER_MAX_WIDTH}>
      <AiComposerSurface
        $variant="popover"
        onKeyDownCapture={handleKeyDownCapture}
        data-testid={suffixTestId('surface', props)}
      >
        <VStack spacing={3} minHeight={0}>
          <HStack spacing={2} align="center">
            <AiComposerMark size={6} />
            <Text size="small" alternative overflowWrap="anywhere">
              {controller.entryTitle}
            </Text>
            <Space fillAvailable />
            <Tooltip label={i18n.t('general.actions.close')}>
              <IconButton
                icon="navigation/close"
                size="small"
                aria-label={i18n.t('general.actions.close')}
                onClick={controller.onPromptClose}
                data-testid={suffixTestId('close', props)}
              />
            </Tooltip>
          </HStack>

          <Text
            size="xSmall"
            color="secondary"
            overflowWrap="anywhere"
            data-testid={suffixTestId('promptHelp', props)}
          >
            {promptHelp}
          </Text>

          <AiComposerPromptInput
            label={promptLabel}
            placeholder={promptLabel}
            value={controller.instruction}
            isDisabled={isBusy}
            onChange={controller.onInstructionChange}
            onSubmit={handleSubmitInstruction}
            data-testid={props['data-testid']}
          />

          <Show when={isGeneratePrompt}>
            <Scroll auto data-testid={suffixTestId('suggestions', props)}>
              <VStack spacing={1}>
                {controller.suggestions.map((suggestion) => (
                  <AiComposerSuggestionButton
                    key={suggestion.key}
                    suggestion={suggestion}
                    isDisabled={isBusy}
                    onSelect={controller.onGenerateSuggestion}
                    data-testid={suffixTestId(`suggestion-${suggestion.key}`, props)}
                  />
                ))}
                <Show when={controller.isRecordContextLoading}>
                  <Box role="status" padding={2}>
                    <HStack spacing={2} align="center">
                      <Spinner size="small" />
                      <Text size="xSmall" color="tertiary">
                        {i18n.t('general.labels.loading')}
                      </Text>
                    </HStack>
                  </Box>
                </Show>
                <Show when={controller.isRecordContextError}>
                  <Alert variant="warning" message={i18n.t('general.labels.error')} />
                </Show>
              </VStack>
            </Scroll>
          </Show>

          <HStack spacing={2} align="center" wrap>
            <Show when={isGeneratePrompt}>
              <Button
                variant="primary"
                size="small"
                leftIcon="action/magic_wand"
                title={i18n.t('entity.order.actions.generate')}
                wrap
                isDisabled={isBusy || !controller.canRunGenerate}
                onClick={controller.onGenerate}
                data-testid={suffixTestId('generate', props)}
              />
            </Show>

            {/* The same pills sit in the footer bar while the popover is closed. */}
            <Show when={controller.hasExistingBody}>{refinementPills}</Show>

            <Show when={controller.canRefineSelection && controller.hasExistingBody}>
              <Button
                variant="ghostLink"
                size="small"
                title={i18n.t('general.aiMessageComposer.actions.refineSelection')}
                wrap
                isDisabled={isBusy || !controller.hasExistingBody}
                onClick={() => controller.onRefineWithInstruction('selection')}
                data-testid={suffixTestId('refine-selection', props)}
              />
            </Show>
          </HStack>

          <Show when={controller.isError}>
            <Alert
              variant="error"
              message={i18n.t(getErrorMessageKey(controller.errorKind))}
              data-testid={suffixTestId('error', props)}
            />
          </Show>
        </VStack>
      </AiComposerSurface>
    </Box>
  );

  const bar = (
    <AiComposerSurface
      $variant="bar"
      onKeyDownCapture={handleKeyDownCapture}
      data-testid={suffixTestId('bar', props)}
    >
      <VStack spacing={2}>
        <Show when={isBusy}>
          <AiComposerFocusRegion role="status" aria-live="polite">
            <HStack spacing={2} align="center">
              <AiComposerMark size={6} />
              <Spinner size="small" />
              <Text size="xSmall" color="secondary" overflowWrap="anywhere">
                {i18n.t('aiAssistant.status.proposing')}
              </Text>
              <Space fillAvailable />
              <Button
                variant="ghostLink"
                size="small"
                title={i18n.t('general.actions.cancel')}
                wrap
                onClick={controller.onStop}
                data-testid={suffixTestId('stop', props)}
              />
            </HStack>
          </AiComposerFocusRegion>
        </Show>

        <Show when={controller.isProposalWithdrawn}>
          <HStack spacing={2} align="center">
            <Box flex={1} minWidth={0}>
              <Alert
                variant="warning"
                message={i18n.t('general.aiMessageComposer.labels.proposalWithdrawn')}
                data-testid={suffixTestId('withdrawn', props)}
              />
            </Box>
            <Tooltip label={i18n.t('general.actions.close')}>
              <IconButton
                icon="navigation/close"
                size="small"
                aria-label={i18n.t('general.actions.close')}
                onClick={controller.onClose}
                data-testid={suffixTestId('closeBar', props)}
              />
            </Tooltip>
          </HStack>
        </Show>

        <Show when={proposal}>
          {(proposal) => (
            <AiComposerProposalBlock
              controller={controller}
              proposal={proposal}
              onKeyDownCapture={handleKeyDownCapture}
              data-testid={props['data-testid']}
            />
          )}
        </Show>
      </VStack>
    </AiComposerSurface>
  );

  // After Replace nothing stays here: the editor footer carries Undo (AiComposerAppliedStatus).
  const hasBar = isBusy || isNotNil(proposal) || controller.isProposalWithdrawn;

  return (
    <Fragment>
      <Show when={isBusy}>
        <AiComposerBusyRing />
      </Show>
      <Show when={controller.isPromptOpen}>
        <AiComposerPopover
          label={controller.entryTitle}
          onClose={controller.onPromptClose}
          data-testid={suffixTestId('prompt', props)}
        >
          {popover}
        </AiComposerPopover>
      </Show>
      <Show when={hasBar}>
        <Box padding={1}>{bar}</Box>
      </Show>
    </Fragment>
  );
}
